/**
 * Rossi Flipbook
 * Page-flip catalog viewer built on pdf.js.
 *
 * Deliberate differences from the A&B Home / DFlip implementation this replaces:
 *  - Lazy init via IntersectionObserver. Nothing loads until the book is near the viewport.
 *    (DFlip binds to window.onload, so its PDF fetch does not even start until ~14s in.)
 *  - CSS 3D page curl instead of three.js + WebGL shadows. ~0 extra KB.
 *  - Landscape PDF pages are auto-split into two leaves, so both spread-built and
 *    single-page PDFs work without re-exporting the source file.
 *  - Single-page mode below a breakpoint, with the canvas sized to its actual container.
 *  - Text-layer search and a thumbnail grid, so a 400-page book is navigable.
 *  - isEvalSupported:false — mitigates CVE-2024-4367 in the pinned pdf.js 3.11.174.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-section-type="rossi-flipbook"]';
  var MAX_DPR = 2;
  var MAX_CANVAS_AREA = 16777216; // iOS Safari hard limit
  var CACHE_LIMIT = 14;

  /* ------------------------------------------------------------------ utils */

  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }

  function idle(fn) {
    if (window.requestIdleCallback) return window.requestIdleCallback(fn, { timeout: 500 });
    return setTimeout(fn, 1);
  }

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  /** Remove all children. Used instead of innerHTML so nothing is ever parsed as markup. */
  function empty(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  /**
   * Soft page-turn, synthesised in two layers so no audio asset ships:
   *  1. the slide — a shaped noise sweep through a rising bandpass, the sound
   *     of paper brushing paper while the leaf is in the air;
   *  2. the settle — a small low sine thump as the page lands.
   * Playback rate is randomised a few percent so consecutive turns don't
   * sound machine-identical.
   */
  function PageSound() {
    this.ctx = null;
  }
  PageSound.prototype.play = function () {
    try {
      if (!this.ctx) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        this.ctx = new AC();
      }
      var ctx = this.ctx;
      if (ctx.state === 'suspended') ctx.resume();
      var now = ctx.currentTime;
      var rate = 0.94 + Math.random() * 0.12;

      var dur = 0.28 * rate;
      var buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
      var data = buf.getChannelData(0);
      for (var i = 0; i < data.length; i++) {
        var t = i / data.length;
        var env = Math.pow(Math.sin(Math.PI * Math.min(1, t * 1.15)), 1.4);
        data[i] = (Math.random() * 2 - 1) * env;
      }
      var src = ctx.createBufferSource();
      src.buffer = buf;
      var bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 0.9;
      bp.frequency.setValueAtTime(900, now);
      bp.frequency.exponentialRampToValueAtTime(2600, now + dur * 0.7);
      bp.frequency.exponentialRampToValueAtTime(1200, now + dur);
      var g1 = ctx.createGain();
      g1.gain.setValueAtTime(0.0001, now);
      g1.gain.exponentialRampToValueAtTime(0.3, now + 0.03);
      g1.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      src.connect(bp).connect(g1).connect(ctx.destination);
      src.start(now);

      var t2 = now + dur * 0.82;
      var osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(170, t2);
      osc.frequency.exponentialRampToValueAtTime(90, t2 + 0.07);
      var g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.0001, t2);
      g2.gain.exponentialRampToValueAtTime(0.16, t2 + 0.012);
      g2.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.09);
      osc.connect(g2).connect(ctx.destination);
      osc.start(t2);
      osc.stop(t2 + 0.1);
    } catch (e) { /* audio is decorative — never let it break navigation */ }
  };

  /* ------------------------------------------------------------- the viewer */

  function Flipbook(root) {
    this.root = root;
    this.opts = JSON.parse(root.getAttribute('data-settings') || '{}');
    this.pdf = null;
    this.leaves = [];          // {pdfPage, half: null|'l'|'r'}
    this.spread = 0;
    this.cache = new Map();    // "leaf@width" -> canvas
    this.textIndex = null;
    this.indexing = false;
    this.animating = false;
    this.zoom = 1;
    this.pan = { x: 0, y: 0 };
    this.sound = new PageSound();
    this.singleMode = false;
    this.destroyed = false;
  }

  /* ---- lifecycle ---- */

  Flipbook.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) return this.boot();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { io.disconnect(); self.boot(); }
      });
    }, { rootMargin: '400px' });
    io.observe(this.root);
  };

  Flipbook.prototype.boot = function () {
    var self = this;
    if (this.booted) return;
    this.booted = true;
    this.buildChrome();
    this.setStatus('Opening the lookbook…');

    this.loadLib()
      .then(function () { return self.loadPdf(); })
      .then(function () { return self.onReady(); })
      .catch(function (err) {
        self.root.classList.remove('rfb--loading');
        self.root.classList.add('rfb--error');
        self.setStatus('This catalog could not be loaded.');
        if (window.console) console.error('[rossi-flipbook]', err);
      });
  };

  Flipbook.prototype.loadLib = function () {
    if (window.pdfjsLib) return Promise.resolve();
    if (Flipbook._libPromise) return Flipbook._libPromise;
    var src = this.opts.libUrl;
    var worker = this.opts.workerUrl;
    Flipbook._libPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        if (!window.pdfjsLib) return reject(new Error('pdfjsLib missing after load'));
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = worker;
        resolve();
      };
      s.onerror = function () { reject(new Error('failed to load ' + src)); };
      document.head.appendChild(s);
    });
    return Flipbook._libPromise;
  };

  Flipbook.prototype.loadPdf = function () {
    var self = this;
    var task = window.pdfjsLib.getDocument({
      url: this.opts.pdfUrl,
      // CVE-2024-4367 mitigation for pinned 3.11.174 — blocks font-driven eval.
      isEvalSupported: false,
      disableAutoFetch: true,   // stream via range requests instead of pulling the whole file
      disableStream: false,
      rangeChunkSize: 262144
    });
    task.onProgress = function (p) {
      if (!p.total) return;
      // Range streaming can report loaded > total; clamp so "102%" never shows.
      var pct = Math.min(100, Math.round((p.loaded / p.total) * 100));
      self.setStatus('Opening the lookbook — ' + pct + '%');
      if (self.ui.loadbarFill) self.ui.loadbarFill.style.width = pct + '%';
    };
    return task.promise.then(function (doc) { self.pdf = doc; });
  };

  Flipbook.prototype.onReady = function () {
    var self = this;
    return this.buildLeaves().then(function () {
      self.root.classList.remove('rfb--loading');
      self.applyMode();
      self.readHash();
      self.bind();
      self.loadOutline();
      idle(function () { self.buildTextIndex(); });
      return self.render();
    });
  };

  /**
   * Map PDF pages onto book leaves. A landscape page is a printed spread and
   * becomes two leaves; a portrait page is one leaf. This is what lets the same
   * section handle an InDesign spread export and a plain single-page PDF.
   */
  Flipbook.prototype.buildLeaves = function () {
    var self = this;
    var n = this.pdf.numPages;
    var pending = [];
    for (var p = 1; p <= n; p++) pending.push(p);

    return Promise.all(pending.map(function (p) {
      return self.pdf.getPage(p).then(function (page) {
        var v = page.getViewport({ scale: 1 });
        return { page: p, ratio: v.width / v.height, w: v.width, h: v.height };
      });
    })).then(function (info) {
      // Decide spread-splitting from the body of the book. Covers are commonly
      // single pages even in a spread-built file, so they must not skew the vote.
      var body = info.length > 2 ? info.slice(1, -1) : info;
      var landscapeMajority = body.filter(function (d) { return d.ratio > 1.2; }).length > body.length / 2;
      self.splitSpreads = self.opts.splitSpreads === 'never' ? false
        : self.opts.splitSpreads === 'always' ? true
        : landscapeMajority;

      self.pageInfo = {};
      info.forEach(function (d) {
        self.pageInfo[d.page] = d;
        if (self.splitSpreads && d.ratio > 1.2) {
          self.leaves.push({ pdfPage: d.page, half: 'l' });
          self.leaves.push({ pdfPage: d.page, half: 'r' });
        } else {
          self.leaves.push({ pdfPage: d.page, half: null });
        }
      });

      // Aspect ratio of a single leaf, taken from a body page rather than the cover.
      var refIdx = Math.min(1, self.leaves.length - 1);
      var ref = self.leaves[refIdx];
      if (ref) {
        var d = self.pageInfo[ref.pdfPage];
        self.leafRatio = (ref.half ? d.w / 2 : d.w) / d.h;
      } else {
        self.leafRatio = 0.75;
      }
    });
  };

  /* ---- pagination ----
   * spread 0        -> [ null, leaf 0 ]        cover alone on the right
   * spread k (k>=1) -> [ leaf 2k-1, leaf 2k ]
   */

  Flipbook.prototype.spreadCount = function () {
    if (this.singleMode) return this.leaves.length;
    return 1 + Math.ceil(Math.max(0, this.leaves.length - 1) / 2);
  };

  Flipbook.prototype.leavesFor = function (spread) {
    if (this.singleMode) return [spread, null];
    if (spread <= 0) return [null, 0];
    return [2 * spread - 1, 2 * spread];
  };

  Flipbook.prototype.spreadForLeaf = function (leafIdx) {
    if (this.singleMode) return clamp(leafIdx, 0, this.leaves.length - 1);
    if (leafIdx <= 0) return 0;
    return Math.ceil(leafIdx / 2);
  };

  /** 1-based page number shown to the user, matching the leaf count. */
  Flipbook.prototype.currentLeaf = function () {
    var pair = this.leavesFor(this.spread);
    var l = pair[1] != null && pair[1] < this.leaves.length ? pair[1] : pair[0];
    return (l || 0) + 1;
  };

  /* ---- chrome ---- */

  Flipbook.prototype.buildChrome = function () {
    var o = this.opts;
    this.root.classList.add('rfb--loading');

    var stage = el('div', 'rfb__stage');
    var book = el('div', 'rfb__book');
    var left = el('div', 'rfb__side rfb__side--left');
    var right = el('div', 'rfb__side rfb__side--right');
    var flip = el('div', 'rfb__flip');
    var flipFront = el('div', 'rfb__flip-face rfb__flip-front');
    var flipBack = el('div', 'rfb__flip-face rfb__flip-back');

    flip.appendChild(flipFront);
    flip.appendChild(flipBack);
    book.appendChild(left);
    book.appendChild(right);
    book.appendChild(flip);
    book.appendChild(el('div', 'rfb__stack-r'));
    stage.appendChild(book);

    var status = el('div', 'rfb__status', { role: 'status', 'aria-live': 'polite' });
    stage.appendChild(status);

    var loadbar = el('div', 'rfb__loadbar');
    var loadbarFill = el('span', 'rfb__loadbar-fill');
    loadbar.appendChild(loadbarFill);
    stage.appendChild(loadbar);

    var navPrev = el('button', 'rfb__edge rfb__edge--prev', { type: 'button', 'aria-label': 'Previous page' });
    var navNext = el('button', 'rfb__edge rfb__edge--next', { type: 'button', 'aria-label': 'Next page' });
    navPrev.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4 7 12l8 8"/></svg>';
    navNext.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4l8 8-8 8"/></svg>';
    stage.appendChild(navPrev);
    stage.appendChild(navNext);

    this.root.appendChild(stage);

    var progress = el('div', 'rfb__progress');
    var progressFill = el('span', 'rfb__progress-fill');
    progress.appendChild(progressFill);
    this.root.appendChild(progress);

    var bar = el('div', 'rfb__bar');
    function btn(name, label, svg) {
      var b = el('button', 'rfb__btn rfb__btn--' + name, { type: 'button', 'aria-label': label, title: label });
      b.innerHTML = svg;
      return b;
    }
    var I = {
      prev: '<svg viewBox="0 0 24 24"><path d="M15 4 7 12l8 8"/></svg>',
      next: '<svg viewBox="0 0 24 24"><path d="M9 4l8 8-8 8"/></svg>',
      first: '<svg viewBox="0 0 24 24"><path d="M18 4l-8 8 8 8M6 4v16"/></svg>',
      last: '<svg viewBox="0 0 24 24"><path d="M6 4l8 8-8 8M18 4v16"/></svg>',
      grid: '<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>',
      list: '<svg viewBox="0 0 24 24"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>',
      search: '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>',
      zoomIn: '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M11 8v6M8 11h6M20 20l-3.5-3.5"/></svg>',
      zoomOut: '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M8 11h6M20 20l-3.5-3.5"/></svg>',
      full: '<svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
      sound: '<svg viewBox="0 0 24 24"><path d="M5 9v6h4l5 4V5L9 9H5z"/></svg>',
      down: '<svg viewBox="0 0 24 24"><path d="M12 4v12M7 12l5 5 5-5M4 20h16"/></svg>',
      share: '<svg viewBox="0 0 24 24"><circle cx="6" cy="12" r="2.6"/><circle cx="17.5" cy="5.5" r="2.6"/><circle cx="17.5" cy="18.5" r="2.6"/><path d="M8.4 10.8 15.1 6.9M8.4 13.2l6.7 3.9"/></svg>'
    };

    this.ui = {};
    this.ui.first = btn('first', 'First page', I.first);
    this.ui.prev = btn('prev', 'Previous page', I.prev);

    var pageBox = el('div', 'rfb__pagebox');
    var pageInput = el('input', 'rfb__pageinput', { type: 'text', inputmode: 'numeric', 'aria-label': 'Page number' });
    var pageTotal = el('span', 'rfb__pagetotal');
    pageBox.appendChild(pageInput);
    pageBox.appendChild(pageTotal);
    this.ui.pageInput = pageInput;
    this.ui.pageTotal = pageTotal;

    this.ui.next = btn('next', 'Next page', I.next);
    this.ui.last = btn('last', 'Last page', I.last);
    this.ui.thumbs = btn('thumbs', 'Thumbnails', I.grid);
    this.ui.outline = btn('outline', 'Contents', I.list);
    this.ui.search = btn('search', 'Search catalog', I.search);
    this.ui.zoomIn = btn('zoomin', 'Zoom in', I.zoomIn);
    this.ui.zoomOut = btn('zoomout', 'Zoom out', I.zoomOut);
    this.ui.full = btn('full', 'Fullscreen', I.full);
    this.ui.sound = btn('sound', 'Page sound', I.sound);
    this.ui.download = btn('download', 'Download PDF', I.down);
    this.ui.share = btn('share', 'Copy link to this page', I.share);

    [this.ui.first, this.ui.prev, pageBox, this.ui.next, this.ui.last,
     el('span', 'rfb__sep'),
     this.ui.outline, this.ui.thumbs, this.ui.search,
     el('span', 'rfb__sep'),
     this.ui.zoomOut, this.ui.zoomIn, this.ui.full
    ].forEach(function (n) { bar.appendChild(n); });

    if (o.share) bar.appendChild(this.ui.share);
    if (o.sound) bar.appendChild(this.ui.sound);
    if (o.download) bar.appendChild(this.ui.download);

    this.root.appendChild(bar);

    var toast = el('div', 'rfb__toast', { role: 'status', 'aria-live': 'polite' });
    this.root.appendChild(toast);

    this.ui.drawer = el('div', 'rfb__drawer', { 'aria-hidden': 'true' });
    this.ui.drawerTitle = el('div', 'rfb__drawer-title');
    var close = el('button', 'rfb__drawer-close', { type: 'button', 'aria-label': 'Close panel' });
    close.innerHTML = '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    this.ui.drawerBody = el('div', 'rfb__drawer-body');
    this.ui.drawer.appendChild(this.ui.drawerTitle);
    this.ui.drawer.appendChild(close);
    this.ui.drawer.appendChild(this.ui.drawerBody);
    this.root.appendChild(this.ui.drawer);
    this.ui.drawerClose = close;

    this.ui.stage = stage;
    this.ui.book = book;
    this.ui.left = left;
    this.ui.right = right;
    this.ui.flip = flip;
    this.ui.flipFront = flipFront;
    this.ui.flipBack = flipBack;
    this.ui.status = status;
    this.ui.edgePrev = navPrev;
    this.ui.edgeNext = navNext;
    this.ui.loadbarFill = loadbarFill;
    this.ui.progressFill = progressFill;
    this.ui.toast = toast;

    // Sound starts ON when the section enables it (a mute toggle is in the bar);
    // audio still only ever plays after a user gesture, so autoplay policy holds.
    this.soundOn = !!o.sound;
    if (this.soundOn) {
      this.ui.sound.classList.add('is-on');
      this.ui.sound.setAttribute('aria-pressed', 'true');
    }
    // Invite the first page turn with a breathing corner peel; any navigation clears it.
    this.root.classList.add('rfb--hint');
  };

  Flipbook.prototype.setStatus = function (msg) {
    if (this.ui && this.ui.status) this.ui.status.textContent = msg || '';
  };

  /* ---- rendering ---- */

  Flipbook.prototype.applyMode = function () {
    var w = this.root.clientWidth;
    var wasSingle = this.singleMode;
    var nowSingle = w < (this.opts.singleBreakpoint || 768);

    if (wasSingle !== nowSingle && this.leaves.length) {
      // Resolve the current leaf under the OLD pagination before switching,
      // otherwise the reader is thrown to a different page on every rotation.
      var leaf = this.currentLeaf() - 1;
      this.singleMode = nowSingle;
      this.spread = this.spreadForLeaf(leaf);
    } else {
      this.singleMode = nowSingle;
    }

    this.root.classList.toggle('rfb--single', this.singleMode);
    this.layout();
  };

  /** Size the book box to the available space, respecting the leaf aspect ratio. */
  Flipbook.prototype.layout = function () {
    var stage = this.ui.stage;
    // Use the content box: clientWidth/Height include CSS padding, and the
    // stage carries extra bottom padding to stay clear of the toolbar pill.
    var cs = window.getComputedStyle(stage);
    var availW = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    var availH = stage.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    if (availW <= 0 || availH <= 0) return;

    var cols = this.singleMode ? 1 : 2;
    var ratio = (this.leafRatio || 0.75) * cols;

    var w = availW;
    var h = w / ratio;
    if (h > availH) {
      h = availH;
      w = h * ratio;
    }
    this.leafW = Math.max(1, Math.floor(w / cols));
    this.leafH = Math.max(1, Math.floor(h));

    this.ui.book.style.width = (this.leafW * cols) + 'px';
    this.ui.book.style.height = this.leafH + 'px';
    this.ui.flip.style.width = this.leafW + 'px';
    this.ui.flip.style.height = this.leafH + 'px';
  };

  Flipbook.prototype.canvasFor = function (leafIdx) {
    var self = this;
    var bucket = Math.ceil(this.leafW / 100) * 100;
    var key = leafIdx + '@' + bucket;
    if (this.cache.has(key)) return Promise.resolve(this.cache.get(key));

    var leaf = this.leaves[leafIdx];
    if (!leaf) return Promise.resolve(null);

    return this.pdf.getPage(leaf.pdfPage).then(function (page) {
      var base = page.getViewport({ scale: 1 });
      var leafW = leaf.half ? base.width / 2 : base.width;
      var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      var scale = (bucket * dpr) / leafW;

      // Respect the iOS canvas area ceiling.
      var projW = leafW * scale, projH = base.height * scale;
      if (projW * projH > MAX_CANVAS_AREA) {
        scale *= Math.sqrt(MAX_CANVAS_AREA / (projW * projH));
      }

      var vp = page.getViewport({ scale: scale });
      var canvas = el('canvas', 'rfb__canvas');
      canvas.width = Math.max(1, Math.round(leaf.half ? vp.width / 2 : vp.width));
      canvas.height = Math.max(1, Math.round(vp.height));

      var ctx = canvas.getContext('2d', { alpha: false });
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      var transform = leaf.half === 'r' ? [1, 0, 0, 1, -vp.width / 2, 0] : null;

      return page.render({ canvasContext: ctx, viewport: vp, transform: transform }).promise
        .then(function () {
          self.cache.set(key, canvas);
          while (self.cache.size > CACHE_LIMIT) {
            self.cache.delete(self.cache.keys().next().value);
          }
          return canvas;
        });
    });
  };

  /** Copy a cached canvas into a host, so the same leaf can appear in two places at once. */
  Flipbook.prototype.paint = function (host, leafIdx) {
    var self = this;
    var missing = leafIdx == null || leafIdx < 0 || leafIdx >= this.leaves.length;
    host.classList.toggle('is-empty', missing);
    if (missing) { empty(host); return Promise.resolve(); }

    return this.canvasFor(leafIdx).then(function (src) {
      if (!src || self.destroyed) return;
      var c = document.createElement('canvas');
      c.className = 'rfb__canvas';
      c.width = src.width;
      c.height = src.height;
      c.getContext('2d').drawImage(src, 0, 0);
      empty(host);
      host.appendChild(c);
    }).catch(function (err) {
      // One page failing to render must not take the whole book down.
      if (window.console) console.warn('[rossi-flipbook] leaf ' + leafIdx + ' failed', err);
    });
  };

  Flipbook.prototype.render = function () {
    var self = this;
    var pair = this.leavesFor(this.spread);
    this.layout();
    this.updateShift();

    var jobs = [this.paint(this.ui.left, pair[0])];
    if (!this.singleMode) {
      jobs.push(this.paint(this.ui.right, pair[1]));
    } else {
      empty(this.ui.right);
      this.ui.right.classList.add('is-empty');
    }

    this.syncBar();
    this.writeHash();

    return Promise.all(jobs).then(function () {
      self.setStatus('');
      self.prefetch();
    });
  };

  Flipbook.prototype.prefetch = function () {
    var self = this;
    var pair = this.leavesFor(this.spread);
    var fwd = (pair[1] != null ? pair[1] : pair[0] || 0);
    var back = (pair[0] != null ? pair[0] : 0);
    var want = [fwd + 1, fwd + 2, back - 1, back - 2];
    idle(function () {
      want.filter(function (i) { return i >= 0 && i < self.leaves.length; })
          .forEach(function (i) { self.canvasFor(i).catch(function () {}); });
    });
  };

  Flipbook.prototype.syncBar = function () {
    this.ui.pageInput.value = String(this.currentLeaf());
    this.ui.pageTotal.textContent = '/ ' + this.leaves.length;
    var atStart = this.spread <= 0;
    var atEnd = this.spread >= this.spreadCount() - 1;
    [this.ui.prev, this.ui.first, this.ui.edgePrev].forEach(function (b) { b.disabled = atStart; });
    [this.ui.next, this.ui.last, this.ui.edgeNext].forEach(function (b) { b.disabled = atEnd; });
    this.ui.zoomOut.disabled = this.zoom <= 1;
    this.ui.zoomIn.disabled = this.zoom >= 3;

    // Reading progress drives the hairline under the stage and the stacked
    // page-edge depth either side of the book.
    var p = this.leaves.length > 1 ? (this.currentLeaf() - 1) / (this.leaves.length - 1) : 0;
    if (this.ui.progressFill) this.ui.progressFill.style.width = (p * 100) + '%';
    this.root.style.setProperty('--rfb-depth-l', (p * 10).toFixed(1) + 'px');
    this.root.style.setProperty('--rfb-depth-r', ((1 - p) * 10).toFixed(1) + 'px');
  };

  /* ---- navigation ---- */

  Flipbook.prototype.go = function (spread, dir) {
    spread = clamp(spread, 0, this.spreadCount() - 1);
    if (spread === this.spread || this.animating) return Promise.resolve();
    this.root.classList.remove('rfb--hint');

    var forward = typeof dir === 'number' ? dir > 0 : spread > this.spread;
    var from = this.spread;
    this.spread = spread;

    if (this.opts.sound && this.soundOn) this.sound.play();

    var self = this;
    var fail = function (err) {
      // Whatever went wrong, leave the book usable.
      self.animating = false;
      self.root.classList.remove('is-flipping');
      if (window.console) console.warn('[rossi-flipbook] page change failed', err);
    };

    if (this.singleMode || this.prefersReducedMotion() || Math.abs(spread - from) > 1) {
      return this.render().catch(fail);
    }
    return this.animate(from, spread, forward).catch(fail);
  };

  Flipbook.prototype.prefersReducedMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  /**
   * One leaf rotates about the spine. Front face is the leaf turning away,
   * back face is the leaf revealed underneath it.
   */
  Flipbook.prototype.animate = function (from, to, forward) {
    var self = this;
    var fromPair = this.leavesFor(from);
    var toPair = this.leavesFor(to);

    var frontLeaf = forward ? fromPair[1] : fromPair[0];
    var backLeaf = forward ? toPair[0] : toPair[1];
    var staticHost = forward ? this.ui.left : this.ui.right;
    var staticLeaf = forward ? fromPair[0] : fromPair[1];
    var revealHost = forward ? this.ui.right : this.ui.left;
    var revealLeaf = forward ? toPair[1] : toPair[0];

    this.animating = true;
    this.root.classList.add('is-flipping');
    var flip = this.ui.flip;
    flip.classList.toggle('rfb__flip--reverse', !forward);

    return new Promise(function (resolve) {
      var settled = false;
      var anim = null;
      var finish = function () {
        if (settled) return;
        settled = true;
        clearTimeout(guard);
        if (anim) { try { anim.cancel(); } catch (e) { /* already done */ } }
        flip.classList.remove('is-active');
        flip.style.transition = 'none';
        flip.style.transform = 'rotateY(0deg)';
        empty(self.ui.flipFront);
        empty(self.ui.flipBack);
        self.root.classList.remove('is-flipping');
        self.animating = false;
        resolve();
      };

      // Armed before any rendering. A slow page or a render failure would
      // otherwise leave animating=true forever and block every later turn.
      var guard = setTimeout(finish, 4000);

      // The page that stays put during the turn can paint immediately.
      self.paint(staticHost, staticLeaf);

      // Starting the turn only needs the two faces of the leaf that moves.
      Promise.all([
        self.paint(self.ui.flipFront, frontLeaf),
        self.paint(self.ui.flipBack, backLeaf)
      ]).then(function () {
        if (settled || self.destroyed) return;

        // The destination page goes in only now, with the leaf about to cover
        // it — painting it earlier spoils the turn with an instant page swap.
        self.paint(revealHost, revealLeaf);

        flip.style.transition = 'none'; // the Web Animation owns the transform
        flip.style.transform = 'rotateY(0deg)';
        flip.classList.add('is-active');

        clearTimeout(guard);
        guard = setTimeout(finish, 900); // turn runs 640ms; this is the safety net

        // Slide the book toward its new resting position while the leaf turns,
        // so entering or leaving the lone cover reads as one motion.
        self.updateShift();

        var target = forward ? 'rotateY(-180deg)' : 'rotateY(180deg)';
        if (flip.animate) {
          // Web Animations API: starts without waiting on a rAF tick and
          // finishes via its own promise — no transitionend to miss.
          anim = flip.animate(
            [{ transform: 'rotateY(0deg)' }, { transform: target }],
            { duration: 640, easing: 'cubic-bezier(0.35, 0.05, 0.22, 1)', fill: 'forwards' }
          );
          anim.onfinish = finish;
          if (anim.finished && anim.finished.then) anim.finished.then(finish, function () {});
        } else {
          // Ancient fallback: the transition + rAF pattern.
          void flip.offsetWidth;
          flip.style.transition = '';
          flip.addEventListener('transitionend', finish);
          requestAnimationFrame(function () {
            flip.style.transform = target;
          });
        }
      });
    }).then(function () {
      return self.render();
    });
  };

  Flipbook.prototype.next = function () { return this.go(this.spread + 1, 1); };
  Flipbook.prototype.prev = function () { return this.go(this.spread - 1, -1); };

  Flipbook.prototype.goToLeaf = function (leafIdx) {
    return this.go(this.spreadForLeaf(clamp(leafIdx, 0, this.leaves.length - 1)));
  };

  /* ---- deep links ---- */

  Flipbook.prototype.hashKey = function () { return 'rfb-' + (this.opts.sectionId || 'book'); };

  Flipbook.prototype.readHash = function () {
    var m = new RegExp(this.hashKey() + '=(\\d+)').exec(window.location.hash || '');
    if (m) this.spread = this.spreadForLeaf(parseInt(m[1], 10) - 1);
  };

  Flipbook.prototype.writeHash = function () {
    if (!this.opts.deepLink) return;
    var key = this.hashKey();
    var hash = (window.location.hash || '').replace(/^#/, '');
    var parts = hash.split('&').filter(function (p) { return p && p.indexOf(key + '=') !== 0; });
    parts.push(key + '=' + this.currentLeaf());
    var next = '#' + parts.join('&');
    if (next !== window.location.hash) {
      try { history.replaceState(null, '', next); } catch (e) { /* older browsers */ }
    }
  };

  /* ---- panels ---- */

  Flipbook.prototype.toast = function (msg) {
    var t = this.ui.toast;
    if (!t) return;
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 1800);
  };

  Flipbook.prototype.openDrawer = function (title, build) {
    this.ui.drawerTitle.textContent = title;
    empty(this.ui.drawerBody);
    this.root.classList.add('rfb--drawer-open');
    this.ui.drawer.setAttribute('aria-hidden', 'false');
    build(this.ui.drawerBody);
  };

  Flipbook.prototype.closeDrawer = function () {
    this.root.classList.remove('rfb--drawer-open');
    this.ui.drawer.setAttribute('aria-hidden', 'true');
  };

  Flipbook.prototype.loadOutline = function () {
    var self = this;
    this.pdf.getOutline().then(function (outline) {
      self.outline = outline && outline.length ? outline : null;
      // A&B ships an outline button that opens an empty panel. With no bookmarks,
      // hide the control rather than offer a dead end.
      if (!self.outline) self.ui.outline.hidden = true;
    }).catch(function () { self.ui.outline.hidden = true; });
  };

  Flipbook.prototype.showOutline = function () {
    var self = this;
    this.openDrawer('Contents', function (body) {
      var list = el('ul', 'rfb__outline');
      var walk = function (items, depth, parent) {
        items.forEach(function (item) {
          var li = el('li', 'rfb__outline-item rfb__outline-item--d' + depth);
          var a = el('button', 'rfb__outline-link', { type: 'button' });
          a.textContent = item.title;
          a.addEventListener('click', function () {
            self.resolveDest(item.dest).then(function (pageIdx) {
              if (pageIdx == null) return;
              var leaf = -1;
              for (var i = 0; i < self.leaves.length; i++) {
                if (self.leaves[i].pdfPage === pageIdx + 1) { leaf = i; break; }
              }
              if (leaf >= 0) { self.goToLeaf(leaf); self.closeDrawer(); }
            });
          });
          li.appendChild(a);
          parent.appendChild(li);
          if (item.items && item.items.length) {
            var sub = el('ul', 'rfb__outline-sub');
            walk(item.items, depth + 1, sub);
            parent.appendChild(sub);
          }
        });
      };
      walk(self.outline || [], 0, list);
      body.appendChild(list);
    });
  };

  Flipbook.prototype.resolveDest = function (dest) {
    var self = this;
    var d = typeof dest === 'string' ? this.pdf.getDestination(dest) : Promise.resolve(dest);
    return Promise.resolve(d).then(function (arr) {
      if (!arr || !arr[0]) return null;
      return self.pdf.getPageIndex(arr[0]);
    }).catch(function () { return null; });
  };

  Flipbook.prototype.showThumbs = function () {
    var self = this;
    this.openDrawer('Pages', function (body) {
      var grid = el('div', 'rfb__thumbgrid');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          self.renderThumb(parseInt(e.target.getAttribute('data-leaf'), 10), e.target);
        });
      }, { root: body, rootMargin: '200px' });

      var currentPair = self.leavesFor(self.spread);
      self.leaves.forEach(function (leaf, i) {
        var cell = el('button', 'rfb__thumb', { type: 'button', 'data-leaf': i, 'aria-label': 'Page ' + (i + 1) });
        cell.style.aspectRatio = String(self.leafRatio || 0.75);
        if (i === currentPair[0] || i === currentPair[1]) cell.classList.add('is-current');
        var num = el('span', 'rfb__thumb-num');
        num.textContent = i + 1;
        cell.appendChild(num);
        cell.addEventListener('click', function () { self.goToLeaf(i); self.closeDrawer(); });
        grid.appendChild(cell);
        io.observe(cell);
      });
      body.appendChild(grid);
      var focus = grid.querySelector('.is-current');
      if (focus) focus.scrollIntoView({ block: 'center' });
    });
  };

  Flipbook.prototype.renderThumb = function (leafIdx, host) {
    var leaf = this.leaves[leafIdx];
    if (!leaf) return;
    this.pdf.getPage(leaf.pdfPage).then(function (page) {
      var base = page.getViewport({ scale: 1 });
      var leafW = leaf.half ? base.width / 2 : base.width;
      var vp = page.getViewport({ scale: 150 / leafW });
      var c = el('canvas', 'rfb__thumb-canvas');
      c.width = Math.max(1, Math.round(leaf.half ? vp.width / 2 : vp.width));
      c.height = Math.max(1, Math.round(vp.height));
      var ctx = c.getContext('2d', { alpha: false });
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, c.width, c.height);
      var transform = leaf.half === 'r' ? [1, 0, 0, 1, -vp.width / 2, 0] : null;
      return page.render({ canvasContext: ctx, viewport: vp, transform: transform }).promise
        .then(function () { host.insertBefore(c, host.firstChild); });
    }).catch(function () { /* a thumbnail failing is not worth surfacing */ });
  };

  /* ---- search ----
   * The point of this panel: A&B renders a 458-page catalog with no text layer
   * and no search, so the only way to reach a product is to already know its page.
   */

  Flipbook.prototype.buildTextIndex = function () {
    var self = this;
    if (this.indexing || this.textIndex) return;
    this.indexing = true;
    this.textIndex = [];

    var p = 1;
    var step = function () {
      if (self.destroyed || p > self.pdf.numPages) { self.indexing = false; return; }
      var page = p++;
      self.pdf.getPage(page).then(function (pg) {
        return pg.getTextContent().then(function (tc) {
          var info = self.pageInfo[page];
          var split = self.splitSpreads && info && info.ratio > 1.2;
          var l = [], r = [], n = [];
          tc.items.forEach(function (item) {
            if (!item.str) return;
            if (!split) { n.push(item.str); return; }
            var x = item.transform ? item.transform[4] : 0;
            (x < info.w / 2 ? l : r).push(item.str);
          });
          if (split) {
            self.textIndex.push({ pdfPage: page, half: 'l', text: l.join(' ') });
            self.textIndex.push({ pdfPage: page, half: 'r', text: r.join(' ') });
          } else {
            self.textIndex.push({ pdfPage: page, half: null, text: n.join(' ') });
          }
          idle(step);
        });
      }).catch(function () { idle(step); });
    };
    idle(step);
  };

  Flipbook.prototype.showSearch = function () {
    var self = this;
    this.buildTextIndex();
    this.openDrawer('Search', function (body) {
      var form = el('form', 'rfb__searchform');
      var input = el('input', 'rfb__searchinput', {
        type: 'search', placeholder: 'Search this catalog…', 'aria-label': 'Search this catalog'
      });
      var results = el('div', 'rfb__results');
      form.appendChild(input);
      body.appendChild(form);
      body.appendChild(results);

      var note = function (msg) {
        var p = el('p', 'rfb__results-note');
        p.textContent = msg;
        results.appendChild(p);
      };

      var run = debounce(function () {
        var q = input.value.trim();
        empty(results);
        if (q.length < 2) return;
        if (!self.textIndex || !self.textIndex.length) {
          return note('Still reading the catalog — try again in a moment.');
        }
        var needle = q.toLowerCase();
        var hits = [];
        self.textIndex.forEach(function (entry) {
          var at = entry.text.toLowerCase().indexOf(needle);
          if (at < 0) return;
          var leafIdx = -1;
          for (var i = 0; i < self.leaves.length; i++) {
            if (self.leaves[i].pdfPage === entry.pdfPage && self.leaves[i].half === entry.half) { leafIdx = i; break; }
          }
          if (leafIdx < 0) return;
          hits.push({ leaf: leafIdx, snippet: entry.text.substr(Math.max(0, at - 40), 110) });
        });

        if (!hits.length) return note('No matches for “' + q + '”.');
        note(hits.length + ' page' + (hits.length === 1 ? '' : 's'));

        hits.sort(function (a, b) { return a.leaf - b.leaf; });
        hits.slice(0, 80).forEach(function (h) {
          var r = el('button', 'rfb__result', { type: 'button' });
          var pg = el('span', 'rfb__result-page');
          pg.textContent = 'p. ' + (h.leaf + 1);
          var sn = el('span', 'rfb__result-snippet');
          sn.textContent = h.snippet;
          r.appendChild(pg);
          r.appendChild(sn);
          r.addEventListener('click', function () { self.goToLeaf(h.leaf); self.closeDrawer(); });
          results.appendChild(r);
        });
      }, 200);

      form.addEventListener('submit', function (e) { e.preventDefault(); run(); });
      input.addEventListener('input', run);
      setTimeout(function () { input.focus(); }, 60);
    });
  };

  /* ---- zoom / pan ---- */

  Flipbook.prototype.applyTransform = function () {
    var shift = this.coverShift || 0;
    this.ui.book.style.transform =
      'translateX(' + shift + '%) scale(' + this.zoom + ') translate(' + this.pan.x + 'px,' + this.pan.y + 'px)';
  };

  /**
   * A lone cover (or back cover) occupies one half of the book box; shift the
   * box a quarter of its width so the visible page reads as centred on stage.
   * The book's transform transition turns the shift into a slide.
   */
  Flipbook.prototype.updateShift = function () {
    var pair = this.leavesFor(this.spread);
    this.coverShift = 0;
    if (!this.singleMode) {
      if (pair[0] == null) this.coverShift = -25;
      else if (pair[1] == null || pair[1] >= this.leaves.length) this.coverShift = 25;
    }
    this.applyTransform();
  };

  Flipbook.prototype.setZoom = function (z) {
    var self = this;
    var prev = this.zoom;
    this.zoom = clamp(z, 1, 3);
    if (this.zoom === 1) this.pan = { x: 0, y: 0 };
    this.applyTransform();
    this.root.classList.toggle('is-zoomed', this.zoom > 1);
    this.syncBar();

    // Re-render sharper once the reader is meaningfully zoomed in.
    var crossed = (prev < 1.5 && this.zoom >= 1.5) || (prev >= 1.5 && this.zoom < 1.5);
    if (crossed) {
      this.cache.clear();
      var realW = this.leafW;
      if (this.zoom >= 1.5) this.leafW = Math.round(realW * this.zoom);
      this.render().then(function () { self.leafW = realW; });
    }
  };

  /* ---- events ---- */

  Flipbook.prototype.bind = function () {
    var self = this;
    var u = this.ui;

    u.next.addEventListener('click', function () { self.next(); });
    u.prev.addEventListener('click', function () { self.prev(); });
    u.edgeNext.addEventListener('click', function () { self.next(); });
    u.edgePrev.addEventListener('click', function () { self.prev(); });
    u.first.addEventListener('click', function () { self.go(0); });
    u.last.addEventListener('click', function () { self.go(self.spreadCount() - 1); });
    u.thumbs.addEventListener('click', function () { self.showThumbs(); });
    u.outline.addEventListener('click', function () { self.showOutline(); });
    u.search.addEventListener('click', function () { self.showSearch(); });
    u.drawerClose.addEventListener('click', function () { self.closeDrawer(); });
    u.zoomIn.addEventListener('click', function () { self.setZoom(self.zoom + 0.5); });
    u.zoomOut.addEventListener('click', function () { self.setZoom(self.zoom - 0.5); });

    u.full.addEventListener('click', function () {
      if (document.fullscreenElement) document.exitFullscreen();
      else if (self.root.requestFullscreen) self.root.requestFullscreen();
    });
    document.addEventListener('fullscreenchange', function () {
      if (self.destroyed) return;
      self.root.classList.toggle('is-fullscreen', document.fullscreenElement === self.root);
      self.applyMode();
      self.cache.clear();
      self.render();
    });

    if (u.sound && this.opts.sound) {
      u.sound.addEventListener('click', function () {
        self.soundOn = !self.soundOn;
        u.sound.classList.toggle('is-on', self.soundOn);
        u.sound.setAttribute('aria-pressed', String(self.soundOn));
      });
    }
    if (u.download && this.opts.download) {
      u.download.addEventListener('click', function () {
        var a = document.createElement('a');
        a.href = self.opts.pdfUrl;
        a.download = '';
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
    }

    if (u.share && this.opts.share) {
      u.share.addEventListener('click', function () {
        var url = window.location.href;
        if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
          navigator.share({ url: url }).catch(function () { /* user dismissed the sheet */ });
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(
            function () { self.toast('Link copied'); },
            function () { window.prompt('Copy this link', url); }
          );
        } else {
          window.prompt('Copy this link', url);
        }
      });
    }

    u.pageInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      var n = parseInt(u.pageInput.value, 10);
      if (!isNaN(n)) self.goToLeaf(n - 1);
      u.pageInput.blur();
    });
    u.pageInput.addEventListener('blur', function () { self.syncBar(); });

    this.root.setAttribute('tabindex', '0');
    this.root.addEventListener('keydown', function (e) {
      if (e.target.tagName === 'INPUT') return;
      switch (e.key) {
        case 'ArrowRight': case 'PageDown': e.preventDefault(); self.next(); break;
        case 'ArrowLeft': case 'PageUp': e.preventDefault(); self.prev(); break;
        case 'Home': e.preventDefault(); self.go(0); break;
        case 'End': e.preventDefault(); self.go(self.spreadCount() - 1); break;
        case 'Escape':
          if (self.root.classList.contains('rfb--drawer-open')) self.closeDrawer();
          break;
      }
    });

    var sx = 0, sy = 0, tracking = false;
    u.stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1 || self.zoom > 1) return;
      tracking = true; sx = e.touches[0].clientX; sy = e.touches[0].clientY;
    }, { passive: true });
    u.stage.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var t = e.changedTouches[0];
      var dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) self.next(); else self.prev();
    }, { passive: true });

    // Double-click resets zoom — the fast exit from a zoomed-in inspection.
    u.stage.addEventListener('dblclick', function (e) {
      if (self.zoom <= 1) return;
      e.preventDefault();
      self.setZoom(1);
    });

    // Click a page half to turn. Ignored while zoomed so panning stays usable.
    u.stage.addEventListener('click', function (e) {
      if (self.zoom > 1) return;
      if (e.target.closest('.rfb__edge, .rfb__bar, .rfb__drawer')) return;
      var r = u.book.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right) return;
      if (e.clientX > r.left + r.width / 2) self.next(); else self.prev();
    });

    var panning = false, px = 0, py = 0, ox = 0, oy = 0;
    u.book.addEventListener('pointerdown', function (e) {
      if (self.zoom <= 1) return;
      panning = true; px = e.clientX; py = e.clientY; ox = self.pan.x; oy = self.pan.y;
      try { u.book.setPointerCapture(e.pointerId); } catch (err) { /* no capture support */ }
    });
    u.book.addEventListener('pointermove', function (e) {
      if (!panning) return;
      self.pan.x = ox + (e.clientX - px) / self.zoom;
      self.pan.y = oy + (e.clientY - py) / self.zoom;
      self.applyTransform();
    });
    u.book.addEventListener('pointerup', function () { panning = false; });
    u.book.addEventListener('pointercancel', function () { panning = false; });

    window.addEventListener('resize', debounce(function () {
      if (self.destroyed || !self.pdf) return;
      var prevW = self.leafW;
      self.applyMode();
      if (self.leafW !== prevW) self.cache.clear();
      self.render();
    }, 180));

    window.addEventListener('hashchange', function () {
      if (self.destroyed || !self.pdf) return;
      var before = self.spread;
      self.readHash();
      if (self.spread !== before) self.render();
    });
  };

  /* ------------------------------------------------------------------- init */

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll(SECTION_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__rfb) return;
      var opts;
      try { opts = JSON.parse(root.getAttribute('data-settings') || '{}'); } catch (e) { return; }
      if (!opts.pdfUrl) return; // nothing configured yet
      var fb = new Flipbook(root);
      root.__rfb = fb;
      fb.observe();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector(SECTION_SELECTOR);
    if (root && root.__rfb) { root.__rfb.destroyed = true; root.__rfb = null; }
  });

  window.RossiFlipbook = Flipbook;
})();